import { useEffect, useRef } from 'react'
import { useQuery, UseQueryResult } from '@tanstack/react-query'

interface AggregationRefreshOptions<TData> {
  /**
   * Query key for the refresh status endpoint
   */
  queryKey: string[]
  /**
   * Function to fetch the refresh status
   */
  queryFn: (signal?: AbortSignal) => Promise<TData>
  /**
   * Function to extract isRefreshing boolean from the response
   */
  getIsRefreshing: (data: TData) => boolean
  /**
   * Callback to trigger when data should be refetched
   * (called on mount and when refreshing transitions to false)
   */
  onRefetch: () => void
  /**
   * Polling interval in milliseconds (default: 5000)
   */
  refetchInterval?: number
  /**
   * Whether the hook is enabled (default: true)
   */
  enabled?: boolean
}

interface AggregationRefreshResult<TData> {
  /**
   * Whether there are pending refreshes
   */
  isRefreshing: boolean
  /**
   * The full refresh status data
   */
  refreshStatus: TData | undefined
  /**
   * The underlying query result for advanced usage
   */
  query: UseQueryResult<TData, Error>
}

/**
 * Hook for managing aggregation refresh status with automatic data refetch.
 *
 * This hook polls an aggregation queue status endpoint and automatically
 * triggers a data refetch when:
 * 1. The component mounts (first status check)
 * 2. isRefreshing transitions from true to false (refresh completed)
 *
 * @example
 * ```tsx
 * const { isRefreshing } = useAggregationRefresh({
 *   queryKey: ['campaigns-refresh-status'],
 *   queryFn: (signal) => getCampaignRefreshStatus(signal),
 *   getIsRefreshing: (data) => data.is_refreshing,
 *   onRefetch: () => refetch(),
 *   refetchInterval: 5000,
 * })
 *
 * // Show spinner when refreshing
 * {isRefreshing && <Spinner />}
 * ```
 */
export function useAggregationRefresh<TData>({
  queryKey,
  queryFn,
  getIsRefreshing,
  onRefetch,
  refetchInterval = 5000,
  enabled = true,
}: AggregationRefreshOptions<TData>): AggregationRefreshResult<TData> {
  const prevIsRefreshingRef = useRef<boolean | null>(null)
  const hasCalledOnMountRef = useRef(false)

  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => queryFn(signal),
    refetchInterval,
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
  })

  const { data: refreshStatus } = query
  const isRefreshing = refreshStatus ? getIsRefreshing(refreshStatus) : false

  // Auto-refetch on mount (first data load) or when isRefreshing transitions true -> false
  useEffect(() => {
    if (refreshStatus === undefined) return

    // Call onRefetch on first data load (mount)
    if (!hasCalledOnMountRef.current) {
      hasCalledOnMountRef.current = true
      onRefetch()
      prevIsRefreshingRef.current = isRefreshing
      return
    }

    const prevIsRefreshing = prevIsRefreshingRef.current

    // Call onRefetch when refreshing just completed (true -> false)
    if (prevIsRefreshing === true && !isRefreshing) {
      onRefetch()
    }

    prevIsRefreshingRef.current = isRefreshing
  }, [isRefreshing, onRefetch, refreshStatus])

  return {
    isRefreshing,
    refreshStatus,
    query,
  }
}
